"use client";

import { createContext, useContext, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useThemeContext } from "./themeContext";
import { useDrawerContext } from "./drawerContext";
import { useCookies } from "react-cookie";
import { fetchWithRefresh } from "../utils/fetchWithRefresh";
import { LogErrors } from "../utils/LogErrors";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { setTheme } = useThemeContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [cookies, setCookie] = useCookies(["refresh_token"]);

  const [user, setFullUser] = useState(
    JSON.parse(localStorage.getItem("user")) || null
  );

  const [participants, setFullParticipants] = useState(
    JSON.parse(localStorage.getItem("participants")) || null
  );

  const [selectedVenue, setterSelectedVenue] = useState(
    JSON.parse(localStorage.getItem("selectedVenue")) || null
  );

  const [selectedZone, setterSelectedZone] = useState(
    JSON.parse(localStorage.getItem("selectedZone")) || null
  );

  const [splashScreen, setterSplashScreen] = useState(
    JSON.parse(localStorage.getItem("splashScreen")) || false
  );

  const [selectedActivityLogParticipant, setterSelectedActivityLogParticipant] = useState(
    JSON.parse(localStorage.getItem("selectedActivityLogParticipant")) || null
  );

  const setSelectedActivityLogParticipant = (participant) => {
    localStorage.setItem("selectedActivityLogParticipant", JSON.stringify(participant));
    setterSelectedActivityLogParticipant(participant);
  }

  const setSelectedZone = (zone) => {
    localStorage.setItem("selectedZone", JSON.stringify(zone));
    setterSelectedZone(zone);
  }

  const setSelectedVenue = (venue) => {
    localStorage.setItem("selectedVenue", JSON.stringify(venue));
    setterSelectedVenue(venue);
  }
  const setSplashScreen = (splashScreen) => {
    localStorage.setItem("splashScreen", JSON.stringify(splashScreen));
    setterSplashScreen(splashScreen);
  }

  const login = (user) => {
    setFullUser(user);
    localStorage.setItem("auth_session", user.token);
    setCookie("refresh_token", user.refreshToken, { path: "/", secure: true, sameSite: "strict", maxAge: 15768000 }); // 6 months in seconds
    localStorage.setItem("user", JSON.stringify(user));
  };

  const setSubscription = (subscription) => {
    setFullUser((prevUser) => {
      const updatedUser = { ...prevUser, subscription };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    }
    );
  };

  const updateTokens = (token, refreshToken) => {
    setFullUser((prevUser) => {
      const updatedUser = { ...prevUser, token, refreshToken };
      localStorage.setItem("auth_session", token);
      setCookie("refresh_token", refreshToken, { path: "/", secure: true, sameSite: "strict", maxAge: 15768000 }); // 6 months in seconds
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const logout = () => {
    localStorage.removeItem("auth_session");
    localStorage.removeItem("refresh_token");
    setCookie("refresh_token", "", { path: "/", expires: new Date(0) });
    //localStorage.removeItem("addToHomeModalShown");
    localStorage.removeItem("user");
    localStorage.removeItem("selectedVenue");
    localStorage.removeItem("selectedZone");
    localStorage.removeItem("strich_device_id");
    localStorage.removeItem("logged-in");
    localStorage.removeItem("participants");
    localStorage.removeItem("splashScreen");
    localStorage.removeItem("activeBandStep2");
    localStorage.removeItem("activeBandStep2Delete");
    localStorage.removeItem("activeBandStep3");
    localStorage.removeItem("activeBandStep3Delete");
    localStorage.removeItem("activeBandStep4");
    localStorage.removeItem("activeBandStep4Delete");
    localStorage.removeItem("activeBandStep5");
    localStorage.removeItem("activeBandStep5Delete");
    localStorage.removeItem("selectedActivityLogParticipant")
    setTheme("");
    setOpen(false);
    setDrawerContent(null);
    setFullUser(null);
    setParticipants(null);
    setSelectedActivityLogParticipant(null)
  };

  const setParticipants = (participants) => {
      localStorage.setItem("participants", JSON.stringify(participants));
      setFullParticipants(participants);
  };

  const refreshSubscription = async ({ onLiteSubscription } = {}) => {
    if (!user?.id || !user?.token) return null;

    return await Sentry.startSpan({ name: "Refresh User Subscription", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Users/FetchUserSubscription?UserLoginId=${user.id}`;
        span.setAttribute("request_url", url);

        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });

        if (!response.ok) {
          const errorText = await response.text();
          Sentry.captureMessage(`Failed to refresh subscription: ${errorText}`, "error");
          LogErrors(user, logout, updateTokens, "Error refreshing subscription: " + errorText);
          return null;
        }

        const data = await response.json();
        setSubscription(data);

        if (data?.subscription?.Name === "Lite" && typeof onLiteSubscription === "function") {
          onLiteSubscription();
        }

        span.setStatus("ok");
        return data;
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error refreshing subscription: " + error);
        return null;
      }
    });
  };

  const refreshParticipants = async () => {
    if (!user?.id || !user?.token) return null;

    return await Sentry.startSpan({ name: "Refresh User Participants", op: "http.request" }, async (span) => {
      try {
        const url = `${import.meta.env.VITE_API_URL}Users/FetchUserParticipants?UserLoginId=${user.id}`;
        span.setAttribute("request_url", url);

        const response = await fetchWithRefresh(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
        }, logout, updateTokens, (message) => { toast.info(message); });

        if (!response.ok) {
          const errorText = await response.text();
          Sentry.captureMessage(`Failed to refresh participants: ${errorText}`, "error");
          LogErrors(user, logout, updateTokens, "Error refreshing participants: " + errorText);
          return null;
        }

        const data = await response.json();
        setParticipants(data);
        span.setStatus("ok");
        return data;
      } catch (error) {
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error refreshing participants: " + error);
        return null;
      }
    });
  };

  return (
    <UserContext.Provider value={{ user, login, logout, updateTokens, setSubscription, refreshSubscription, refreshParticipants, selectedVenue, selectedZone, setSelectedVenue, setSelectedZone, participants, setParticipants, splashScreen, setSplashScreen, selectedActivityLogParticipant, setSelectedActivityLogParticipant }}>
      {children}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
